import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CHECKOUT_URL } from './CTAButton';
import { PRICE } from './salesData';
import { trackInitiateCheckout } from '../utils/metaPixel';

/** "$12.990" → "AR$ 12.990" (mismo precio que el resto de la landing). */
const PRICE_LABEL = `AR$ ${PRICE.current.replace('$', '')}`;

/** Scroll mínimo (px) para mostrar la barra. */
const SHOW_AFTER_PX = 100;

/** Lee el scroll vertical sin importar qué elemento sea el que scrollea. */
function getScrollTop(): number {
  return Math.max(window.scrollY || 0, document.documentElement?.scrollTop || 0, document.body?.scrollTop || 0);
}

/** ¿Hay un CTA principal ocupando la pantalla? (bloque de oferta o footer) */
function blockedByMainCta(): boolean {
  const offer = document.getElementById('comprar');
  if (!offer) return false;
  const rect = offer.getBoundingClientRect();
  return rect.top < window.innerHeight * 0.8 && rect.bottom > window.innerHeight * 0.2;
}

/**
 * Barra de compra fija, en mobile, tablet y desktop. Se monta con un portal
 * directo en <body> para que ningún contenedor con overflow o transform pueda
 * ocultarla, y se retira cuando ya hay un CTA principal en pantalla.
 */
export function StickyBuyBar() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const scrolledDown = getScrollTop() > SHOW_AFTER_PX;
      const footer = document.getElementById('site-footer');
      const footerReached = footer ? footer.getBoundingClientRect().top <= window.innerHeight - 40 : false;
      setVisible(scrolledDown && !footerReached && !blockedByMainCta());
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    update();
    // `capture: true` en document también recibe el scroll de contenedores
    // internos (los eventos de scroll no burbujean, pero sí capturan).
    document.addEventListener('scroll', onScroll, {
      passive: true,
      capture: true
    });
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('resize', onScroll);
    window.addEventListener('orientationchange', onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('orientationchange', onScroll);
    };
  }, []);
  if (typeof document === 'undefined') return null;
  return createPortal(<div aria-hidden={!visible} className={`fixed inset-x-0 bottom-0 z-[9999] transition-transform duration-300 ease-out ${visible ? 'translate-y-0' : 'pointer-events-none translate-y-[130%]'}`} style={{
    paddingBottom: 'env(safe-area-inset-bottom)'
  }} data-id="element-207" data-mp="37q0upfod36xt">
      <div className="mx-auto w-full sm:max-w-2xl sm:px-5 sm:pb-4" data-id="element-208" data-mp="3btgm9xlkqh3n">
        <div className="border-t border-graphite-line bg-ink/95 px-3 pb-2.5 pt-2 shadow-[0_-10px_28px_-14px_rgba(0,0,0,0.6)] backdrop-blur sm:flex sm:items-center sm:justify-between sm:gap-4 sm:rounded-2xl sm:border sm:px-4 sm:py-3" data-id="element-209" data-mp="3vdfmib8o0sd6">
          <div className="text-center sm:min-w-0 sm:text-left" data-id="element-210" data-mp="3mcpqp7jen1zg">
            <p className="font-display text-[0.62rem] font-bold uppercase tracking-[0.08em] text-white/60 sm:text-[0.68rem]" data-id="element-211" data-mp="3qeb918epk8h1">
              100 Scripts de WhatsApp
            </p>
            <p className="hidden font-display text-[1.05rem] font-extrabold leading-none tracking-tight tabular-nums text-white sm:mt-1 sm:block" data-id="element-212" data-mp="3he66pcodg09q">
              {PRICE_LABEL}
            </p>
          </div>

          <a href={CHECKOUT_URL} onClick={() => trackInitiateCheckout()} tabIndex={visible ? 0 : -1} className="mt-1.5 flex min-h-[52px] w-full items-center justify-center rounded-xl bg-wa px-3 py-2.5 text-center font-display text-[0.84rem] font-bold uppercase leading-[1.15] tracking-[0.03em] text-ink shadow-[0_12px_26px_-12px_rgba(11,107,52,0.7)] transition-transform duration-200 ease-out active:scale-[0.99] focus:outline-none focus-visible:ring-4 focus-visible:ring-wa/50 sm:mt-0 sm:min-h-[48px] sm:w-auto sm:shrink-0 sm:px-6 sm:text-[0.8rem]" data-id="element-213" data-mp="37o3br6xowc8r">
            <span className="sm:hidden" data-id="element-214" data-mp="2cw3sc7b1wxyx">Quiero los 100 scripts • {PRICE_LABEL}</span>
            <span className="hidden sm:inline" data-id="element-215" data-mp="27w7s8j5h5qon">Quiero los 100 scripts</span>
          </a>
        </div>
      </div>
    </div>, document.body);
}